import {
  Box,
  Flex,
  Heading,
  Spacer,
  Text,
  HStack,
  VStack,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { StatusBadge } from './StatusBadge';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { VerificationPipelineStatusGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import {
  BreadcrumbProps,
  Breadcrumbs,
} from 'packages/common-frontend/components/Breadcrumbs';
import { UploadDrawerButton } from 'packages/dataPipeline/ui/uploads/UploadDrawerButton';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';

export const reportingDateQueryParam = 'reportingDate';

export const VerificationMetadataEntry = ({
  heading,
  value,
}: {
  heading: string;
  value: string;
}) => {
  return (
    <VStack align="flex-start">
      <Text fontWeight={400} fontSize="14px" textColor={colors.black[70]}>
        {heading}
      </Text>
      <Text>{value}</Text>
    </VStack>
  );
};

export const VerificationsTemplateV2 = ({
  children,
  status,
  selectedCreditFacilityId,
  selectedReportingDate,
  selectedFileVersion,
}: {
  children: React.ReactNode;
  status: VerificationPipelineStatusGQLType | null;
  selectedCreditFacilityId: string;
  selectedReportingDate: Util.FullDateStr;
  selectedFileVersion: number;
}) => {
  const { displayOptions } = useVerificationOptions();
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityName = facilities.find(
    (facility) => facility.id === selectedCreditFacilityId,
  )?.name;

  finleyAssert(
    creditFacilityName,
    `Credit facility name not found for [id:${selectedCreditFacilityId}] in [${facilities
      .map(({ id }) => id)
      .join(', ')}]`,
  );

  if (!displayOptions.showTopLevelHeader) {
    return <>{children}</>;
  }

  const breadcrumbs: BreadcrumbProps[] = [
    {
      label: 'Funding Request',
      path: '/funding-requests/',
    },
    {
      label: 'Verification',
    },
  ];

  return (
    <Box>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Flex mt="16px">
        <Box>
          <Flex>
            <Heading as="h2" mr="20px">
              Verify Funding Request
            </Heading>
            {status && <StatusBadge value={status} />}
          </Flex>
        </Box>
        <Spacer />
        <Box>
          <UploadDrawerButton
            buttonText="Upload New Version"
            creditFacilityId={selectedCreditFacilityId}
            reportingDate={selectedReportingDate}
          />
        </Box>
      </Flex>
      <HStack mt="64px" spacing="180px">
        <VerificationMetadataEntry
          heading="Credit Facility"
          value={creditFacilityName}
        />
        <VerificationMetadataEntry
          heading="Reporting Date"
          value={selectedReportingDate}
        />
        <VerificationMetadataEntry
          heading="Version"
          value={String(selectedFileVersion)}
        />
      </HStack>
      <Box mt="64px">{children}</Box>
    </Box>
  );
};
